import { Button, Card, CardContent, IconButton } from '@mui/material';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import type {
  SearchPeriod,
  SearchSiteView,
} from '../../../api/src/search-metrics/search-metrics.types';
import { Box, Stack, Typography } from '../components/mui-system';
import {
  MetricsChart,
  MetricsValues,
  SourceCoverage,
  compactMetric,
  metric,
  metricNames,
  number,
  sourceNames,
  type ChartMetric,
  type ChartMode,
} from './search-metrics-presentation';
export type SiteCardMode = 'large' | 'mini';

export function MetricsSiteCard({
  site,
  period,
  kind,
  mode = 'large',
  onDetails,
}: {
  site: SearchSiteView;
  period: SearchPeriod;
  kind: ChartMode;
  mode?: SiteCardMode;
  onDetails(): void;
}) {
  const multipleSources = site.sources.length > 1;
  const mini = mode === 'mini';
  return (
    <Card component="article" aria-label={site.siteKey} variant="outlined" sx={{ minWidth: 0 }}>
      <CardContent sx={{ p: mini ? 1.5 : '18px', '&:last-child': { pb: 0 } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
          mb={mini ? 1 : 2}
        >
          <Stack direction="row" alignItems="center" gap={0.75} minWidth={0}>
            {!mini &&
              site.sources.map((source) => (
                <SourceCoverage key={source.property.id} source={source} />
              ))}
            <Typography
              fontWeight={800}
              fontSize={mini ? 14 : undefined}
              title={site.siteKey}
              sx={
                mini
                  ? {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                    }
                  : { overflowWrap: 'anywhere', minWidth: 0 }
              }
            >
              {site.siteKey}
            </Typography>
          </Stack>
          {mini ? (
            <IconButton
              size="small"
              onClick={onDetails}
              aria-label={`Подробнее ${site.siteKey}`}
              title="Подробнее"
              sx={{ flexShrink: 0 }}
            >
              <ArrowOutwardIcon sx={{ fontSize: 18 }} />
            </IconButton>
          ) : (
            <Button
              size="small"
              onClick={onDetails}
              aria-label={`Подробнее ${site.siteKey}`}
              sx={{ flexShrink: 0 }}
            >
              Подробнее
            </Button>
          )}
        </Stack>
        {mini ? (
          <MiniValues site={site} />
        ) : (
          <Stack gap={2} mb={2}>
            {site.sources.map((source) => (
              <Box key={source.property.id}>
                {multipleSources && (
                  <Typography fontSize={11} fontWeight={600} color="text.secondary" mb={0.75}>
                    {sourceNames[source.property.provider]}
                  </Typography>
                )}
                <MetricsValues
                  value={source.current}
                  change={source.clickChange}
                  showCoverage={false}
                />
              </Box>
            ))}
          </Stack>
        )}
        <Box mx={mini ? -1.5 : '-18px'}>
          <MetricsChart
            sources={site.sources}
            period={period}
            kind={kind}
            compact={mini}
            edgeToEdge
          />
        </Box>
      </CardContent>
    </Card>
  );
}

function MiniValues({ site }: { site: SearchSiteView }) {
  const metrics = Object.keys(metricNames) as ChartMetric[];
  return (
    <Box
      component="table"
      aria-label={`Показатели ${site.siteKey}`}
      sx={{
        width: '100%',
        tableLayout: 'fixed',
        borderCollapse: 'collapse',
        mb: 1,
        '& th, & td': { textAlign: 'right', py: 0.5, px: 0.25 },
        '& th:first-of-type': { width: 36, textAlign: 'left' },
        '& thead th:nth-of-type(4)': { width: '26%' },
        '& thead th:nth-of-type(5)': { width: '18%' },
      }}
    >
      <thead>
        <tr>
          <Box component="th" scope="col" aria-label="Источник" />
          {metrics.map((key) => (
            <Typography
              component="th"
              scope="col"
              key={key}
              fontSize={10}
              fontWeight={500}
              color="text.secondary"
            >
              {key === 'position' ? 'Ср. поз.' : metricNames[key]}
            </Typography>
          ))}
        </tr>
      </thead>
      <tbody>
        {site.sources.map((source) => (
          <tr key={source.property.id}>
            <Box component="th" scope="row">
              <SourceCoverage source={source} />
            </Box>
            {metrics.map((key) => (
              <Box component="td" key={key} sx={{ verticalAlign: 'top' }}>
                <Typography
                  fontWeight={700}
                  fontSize={14}
                  title={metric(source.current[key], key)}
                  sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                >
                  {compactMetric(source.current[key], key)}
                </Typography>
                {key === 'clicks' && source.clickChange !== null && (
                  <Typography
                    fontSize={10}
                    color={source.clickChange >= 0 ? 'success.main' : 'error.main'}
                    title="Изменение кликов к предыдущему периоду"
                  >
                    {source.clickChange > 0 ? '+' : ''}
                    {number(source.clickChange, 1)}%
                  </Typography>
                )}
              </Box>
            ))}
          </tr>
        ))}
      </tbody>
    </Box>
  );
}
